import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { customerService, invoiceService } from '../services/firebase';
import { Customer, InvoiceForm as InvoiceFormType, InvoiceItem } from '../types';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from './LoadingSpinner';

const defaultItem: InvoiceItem = { id: '', description: '', quantity: 1, unitPrice: 0, total: 0, freight: 0, weight: 0 };

const InvoiceForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, control, handleSubmit, watch, formState: { errors } } = useForm<InvoiceFormType>({
    defaultValues: {
      customerId: '',
      items: [defaultItem],
      tax: 0,
      currency: 'USD',
      notes: '',
      dueDate: new Date(),
      freight: 0,
      weight: 0,
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchItems = watch('items');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const fetchedCustomers = await customerService.getAllCustomers();
        setCustomers(fetchedCustomers);
      } catch (err) {
        setError('Failed to fetch customers.');
        console.error(err);
      }
    };
    fetchCustomers();
  }, []);

  const onSubmit = async (data: InvoiceFormType) => {
    if (!user) {
      setError('You must be logged in to create an invoice.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Ensure items are correctly formatted
      const formattedData = {
        ...data,
        items: data.items.map(item => ({
          id: '',
          description: item.description,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          total: (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0),
          freight: Number(item.freight) || 0,
          weight: Number(item.weight) || 0,
        })),
        tax: Number(data.tax) || 0,
        dueDate: new Date(data.dueDate),
        documents: [],
        freight: Number(data.freight) || 0,
        weight: Number(data.weight) || 0,
      };
      const newInvoice = await invoiceService.createInvoice(formattedData, user.id);

      // Send notification email
      try {
        await fetch('/api/notify/invoice', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Include auth token if your API is protected
          },
          body: JSON.stringify({
            customerId: newInvoice.customerId,
            invoiceId: newInvoice.id,
          }),
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
        // Don't block the UI for this, but log it.
      }

      navigate('/invoices');
    } catch (err) {
      setError('Failed to create invoice. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const subtotal = watchItems.reduce((acc, item) => acc + (Number(item?.quantity) || 0) * (Number(item?.unitPrice) || 0), 0);
  const taxValue = watch('tax');
  const taxAmount = subtotal * (Number(taxValue) || 0) / 100;
  const total = subtotal + taxAmount;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Invoice Details</h2>

      {error && <p className="text-red-500 bg-red-100 p-3 rounded mb-4">{error}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="customerId" className="block text-gray-700 text-sm font-bold mb-2">Customer</label>
          <select
            id="customerId"
            {...register('customerId', { required: 'Customer is required' })}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          >
            <option value="" disabled>Select a customer</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>
          {errors.customerId && <p className="text-red-500 text-xs italic">{errors.customerId.message}</p>}
        </div>
        <div>
          <label htmlFor="dueDate" className="block text-gray-700 text-sm font-bold mb-2">Due Date</label>
          <input
            type="date"
            id="dueDate"
            {...register('dueDate', { required: 'Due date is required', valueAsDate: true })}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
           {errors.dueDate && <p className="text-red-500 text-xs italic">{errors.dueDate.message}</p>}
        </div>
      </div>


      <div className="mb-6">
        <h3 className="text-xl font-bold mb-4">Invoice Items</h3>
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-4 p-4 border rounded-lg">
              <input
                {...register(`items.${index}.description`, { required: true })}
                placeholder="Description"
                className="w-1/4 p-2 border rounded"
              />
              <input
                type="number"
                {...register(`items.${index}.quantity`, { valueAsNumber: true, min: 1 })}
                placeholder="Qty"
                className="w-1/12 p-2 border rounded"
              />
              <input
                type="number"
                step="0.01"
                {...register(`items.${index}.unitPrice`, { valueAsNumber: true, min: 0 })}
                placeholder="Unit Price"
                className="w-1/6 p-2 border rounded"
              />
              <input
                type="number"
                step="0.01"
                {...register(`items.${index}.freight`, { valueAsNumber: true, min: 0 })}
                placeholder="Freight"
                className="w-1/6 p-2 border rounded"
              />
              <input
                type="number"
                step="0.01"
                {...register(`items.${index}.weight`, { valueAsNumber: true, min: 0 })}
                placeholder="Weight"
                className="w-1/6 p-2 border rounded"
              />
              <div className="w-1/6 p-2 font-semibold">
                ${((Number(watchItems[index]?.quantity) || 0) * (Number(watchItems[index]?.unitPrice) || 0)).toFixed(2)}
              </div>
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 font-bold">
                Remove
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => append(defaultItem)} className="mt-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
          Add Item
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-2">
          <label htmlFor="notes" className="block text-gray-700 text-sm font-bold mb-2">Notes</label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={4}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
                <span className="font-bold">Subtotal:</span>
                <span className="font-semibold">${subtotal.toFixed(2)}</span>
            </div>
             <div className="flex justify-between items-center">
                <label htmlFor="tax" className="font-bold">Tax (%):</label>
                <input
                    type="number"
                    id="tax"
                    step="0.1"
                    {...register('tax', { valueAsNumber: true, min: 0 })}
                    className="w-24 p-2 border rounded text-right"
                />
            </div>
            <hr/>
             <div className="flex justify-between items-center text-xl font-bold">
                <span>Total:</span>
                <span className="font-semibold">${total.toFixed(2)}</span>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="freight" className="block text-gray-700 text-sm font-bold mb-2">Freight</label>
          <input
            type="number"
            id="freight"
            step="0.01"
            {...register('freight', { valueAsNumber: true, min: 0 })}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div>
          <label htmlFor="weight" className="block text-gray-700 text-sm font-bold mb-2">Weight</label>
          <input
            type="number"
            id="weight"
            step="0.01"
            {...register('weight', { valueAsNumber: true, min: 0 })}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
      </div>

      <div className="flex items-center justify-end">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg focus:outline-none focus:shadow-outline flex items-center"
        >
          {loading && <LoadingSpinner />}
          {loading ? 'Creating...' : 'Create Invoice'}
        </button>
      </div>
    </form>
  );
};

export default InvoiceForm; 